import { Component, EventEmitter, Injector, Input, OnDestroy, OnInit, Output } from '@angular/core';
import {
  faPrint,
  faPaperPlane,
  faCalendar,
  faSave,
  faList,
  faLock,
  faPercentage,
  faUnlock,
} from '@fortawesome/free-solid-svg-icons';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { CONFIG_CONTEXT } from 'src/app/enum/config-context.enum';
import { StatisticTypeEnum } from 'src/app/enum/statistic-type.enum';
import { KitchenService } from 'src/app/services/configurators-2d/applications/kitchen.service';
import { CurrentService } from 'src/app/services/current.service';
import { CustomerService } from 'src/app/services/customer.service';
import { MODAL, ModalService } from 'src/app/services/modal.service';
import { ProcessQuoteService } from 'src/app/services/process-quote.service';
import { ProjectService } from 'src/app/services/project.service';
import { StatisticService } from 'src/app/services/statistic.service';
import { TunnelService } from 'src/app/services/tunnel.service';
import { DiscountTypeEnum } from 'src/app/enum/discount-type.enum';
import { cloneDeep } from 'src/app/services/helpers/utils';
import { PdfViewModel } from 'src/app/models/pdf-view-model';
import { PrintService } from 'src/app/services/print.service';
import { ToastService } from 'src/app/services/toast.service';
import { VALIDATION_COMPONENT_TYPE_ENUM } from '../validation.component';
import { BaseValidationSideBarActionsModel } from 'src/app/models/configurators-2d/applications/kitchen/validation/base-validation-project-info';
import { StorageService } from 'src/app/services/configurators-2d/applications/storage.service';
import { logError } from 'src/app/services/helpers/sentry.helper';
import { FeatureSettingsService } from 'src/app/services/feature-settings/feature-settings.service';

@Component({
  selector: 'ng-validation-side-bar-actions',
  templateUrl: './validation-side-bar-actions.component.html',
})
export class ValidationSideBarActionsComponent implements OnInit, OnDestroy {
  @Input() model$: Observable<BaseValidationSideBarActionsModel>;
  private _modelSubscription: Subscription;

  @Input() loading$: Observable<boolean>;
  private _loadingSubscription: Subscription;

  @Output() onRefreshRequested: EventEmitter<VALIDATION_COMPONENT_TYPE_ENUM> = new EventEmitter();

  public model: BaseValidationSideBarActionsModel;
  private loading: boolean;

  private sendingToComPlusLoading: boolean;
  private saveLoading: boolean;
  public printLoading: boolean;

  private discountValueSelected: BehaviorSubject<DiscountTypeEnum> = new BehaviorSubject<DiscountTypeEnum>(undefined);
  private _discountValueSubscription: Subscription;

  //ICON
  faPrint = faPrint;
  faPaperPlane = faPaperPlane;
  faCalendar = faCalendar;
  faSave = faSave;
  faList = faList;
  faLock = faLock;
  faPercentage = faPercentage;
  faUnlock = faUnlock;
  protected readonly featureSettingsService = this.injector.get(FeatureSettingsService);

  get buttonsLoading() {
    return this.sendingToComPlusLoading || this.saveLoading || this.loading;
  }

  get locked(): boolean {
    return this.model?.locked;
  }

  get isPrevisit() {
    const tunnelService = this.injector.get(TunnelService);
    return tunnelService.workbenchTunnelData.isCosentino;
  }

  get currentContext(): string {
    return this.injector.get(CustomerService).context;
  }

  get isCurrentContextKitchen(): boolean {
    return this.currentContext === CONFIG_CONTEXT.KITCHEN;
  }

  get isCurrentContextStorage(): boolean {
    return this.currentContext === CONFIG_CONTEXT.STORAGE;
  }

  constructor(
    public readonly injector: Injector,
    private readonly storageService: StorageService
  ) { }

  ngOnInit(): void {
    this.subscribeToObservables();
  }

  ngOnDestroy(): void {
    this._modelSubscription?.unsubscribe();
    this._loadingSubscription?.unsubscribe();
    this._discountValueSubscription?.unsubscribe();
  }

  private async generateXML(): Promise<string> {
    const currentService = this.injector.get(CurrentService);
    const customerService = this.injector.get(CustomerService);
    let xml = '';
    const project = currentService.HBMProject;
    if (customerService.context == CONFIG_CONTEXT.KITCHEN) {
      const kitchenService = this.injector.get(KitchenService);
      const res = await kitchenService.generateXML(project.uuid);
      xml = res.xml;
    } else if (customerService.context == CONFIG_CONTEXT.STORAGE) {
      const storageService = this.injector.get(StorageService);
      const res = await storageService.generateXML(project.uuid);
      xml = res.xml;
    }

    return xml;
  }

  private async sendStorageComPlus(xml: string): Promise<number> {
    const translateService = this.injector.get(TranslateService);
    const processQuoteService = this.injector.get(ProcessQuoteService);

    let opportunityNumber;
    try {
      opportunityNumber = await processQuoteService.sendDotComPlusOrder(xml);
      return opportunityNumber;
    } catch (error: any) {
      throw translateService.instant('validation.com_plus_error.' + this.currentContext + '_send_error', {
        error: JSON.stringify(error),
      });
    }
  }

  async sendToComPlus() {
    const currentService = this.injector.get(CurrentService);
    const projectService = this.injector.get(ProjectService);
    const tunnelService = this.injector.get(TunnelService);
    const modalService = this.injector.get(ModalService);

    let _dotComPlusProcessQuoteXml: string = await this.generateXML();
    let storageOpportunityNumber: number;
    this.sendingToComPlusLoading = true;
    try {
      storageOpportunityNumber = await this.sendStorageComPlus(_dotComPlusProcessQuoteXml);
    } catch (error) {
      this.sendingToComPlusLoading = false;
      this.handleError(JSON.stringify(error));
      return;
    }

    tunnelService.storageTunnelData.opportunityNumber = storageOpportunityNumber;

    try {
      await projectService.lock(currentService.HBMProject.uuid);
      currentService.HBMProject.locked = true;
    } catch (error: any) {
      this.sendingToComPlusLoading = false;
      this.handleError(JSON.stringify(error));
    }

    try {
      await this.save(false);
    } catch (error) {
      this.sendingToComPlusLoading = false;
      this.handleError(JSON.stringify(error));
    }

    this.sendingToComPlusLoading = false;
    modalService.display(MODAL.ORDER_CONFIRM, {
      opportunityNumber: storageOpportunityNumber,
    });

    this.onRefreshRequested.emit();
  }

  private displaySaveModal(titleRef: string) {
    const modalService = this.injector.get(ModalService);

    modalService.display(MODAL.MESSAGE, {
      titleRef: titleRef,
      contentRef: 'Mise à jour effectuée',
      buttons: [
        {
          labelRef: 'Ok',
          primary: true,
          action: () => {
            modalService.close();
            this.sendSavingCountStatistic();
          },
        },
      ],
    });
  }

  private async updateConfiguration(requestContent: any) {
    if (this.isCurrentContextKitchen) {
      const kitchenService = this.injector.get(KitchenService);
      try {
        await kitchenService.updateKitchenConfiguration(requestContent);
      } catch (error) {
        throw JSON.stringify(error);
      }
    } else if (this.isCurrentContextStorage) {
      const storageService = this.injector.get(StorageService);
      try {
        await storageService.updateStorageConfiguration(requestContent);
      } catch (error) {
        throw JSON.stringify(error);
      }
    }
  }

  private async saveConfiguration(requestContent: any) {
    const kitchenService = this.injector.get(KitchenService);
    try {
      await kitchenService.createKitchen(requestContent);
    } catch (error) {
      throw JSON.stringify(error);
    }
  }

  async save(withModal: boolean = true) {
    this.saveLoading = true;
    const tunnelService = this.injector.get(TunnelService);

    if (this.isCurrentContextKitchen) {
      let requestContent = this.buildKitchenTunnelData();
      if (tunnelService.kitchenTunnelData.configId) {
        requestContent.id = tunnelService.kitchenTunnelData.configId;
        try {
          await this.updateConfiguration(requestContent);
          if (withModal) {
            this.showSuccessToast('Succès de la mise à jour');
          }
        } catch (error) {
          this.handleError(error);
        } finally {
          this.saveLoading = false;
        }
      } else {
        try {
          await this.saveConfiguration(requestContent);
          if (withModal) {
            this.showSuccessToast('Succès de la sauvegarde');
          }
        } catch (error) {
          this.handleError(error);
        } finally {
          this.saveLoading = false;
        }
      }
    } else if (this.isCurrentContextStorage) {
      let requestContent = this.buildStorageTunnelData();
      try {
        await this.updateConfiguration(requestContent);
        if (withModal) {
          this.showSuccessToast('Succès de la mise à jour');
        }
      } catch (error) {
        this.handleError(error);
      } finally {
        this.saveLoading = false;
        this.sendSavingCountStatistic();
      }
    }
  }

  private handleError(errorMessage: string) {
    logError(errorMessage);
    this.showErrorToast(errorMessage);
  }

  private buildStorageTunnelData() {
    const storageTunnelData = this.injector.get(TunnelService).storageTunnelData;
    const currentService = this.injector.get(CurrentService);
    const customerService = this.injector.get(CustomerService);

    const storageTunnel: any = {
      bom: JSON.stringify(storageTunnelData.bom),
      projectUUID: storageTunnelData.HBMProjectUUID,
      id: storageTunnelData.configId,
      montantTotal: storageTunnelData.totalFundingCost,
      mensuality: storageTunnelData.mensuality,
      duration: storageTunnelData.duration,
      offers: JSON.stringify(storageTunnelData.offerTable),
      downPayment: storageTunnelData.downPayment,
      totalPriceBom: storageTunnelData.totalPriceBom,
      discount: parseInt(storageTunnelData.discountValue.match(/\d+/)[0], 10),
      opportunity: storageTunnelData.opportunityNumber,
      title: currentService.HBMProject.name,
      customerId: customerService.userDatas.id,
      creatorId: currentService.vendor.sapId,
      interest: storageTunnelData.interest,
      vac: storageTunnelData.vac,
      txtAssurance: storageTunnelData.txtAssurance,
      mlData: JSON.stringify(storageTunnelData.mlData),
    };

    return storageTunnel;
  }

  private buildKitchenTunnelData() {
    const currentService = this.injector.get(CurrentService);
    const tunnelService = this.injector.get(TunnelService);
    const customerService = this.injector.get(CustomerService);

    const discountValue = tunnelService.kitchenTunnelData.discountValue;

    const kitchenTunnelData: any = {
      projectUUID: currentService.HBMProject.uuid,
      isLess2Years: tunnelService.kitchenTunnelData.isLess2Years,
      withInstallation: tunnelService.kitchenTunnelData.withInstallation,
      downPayment: tunnelService.kitchenTunnelData.downPayment,
      discount: parseInt(discountValue.match(/\d+/)[0], 10),
      mensuality: tunnelService.kitchenTunnelData.withInstallation
        ? tunnelService.kitchenTunnelData.mensualityWithInstallation
        : tunnelService.kitchenTunnelData.mensualityWithoutInstallation,
      otherMensuality: !tunnelService.kitchenTunnelData.withInstallation
        ? tunnelService.kitchenTunnelData.mensualityWithInstallation
        : tunnelService.kitchenTunnelData.mensualityWithoutInstallation,

      duration: tunnelService.kitchenTunnelData.withInstallation
        ? tunnelService.kitchenTunnelData.durationWithInstallation
        : tunnelService.kitchenTunnelData.durationWithoutInstallation,
      otherDuration: !tunnelService.kitchenTunnelData.withInstallation
        ? tunnelService.kitchenTunnelData.durationWithInstallation
        : tunnelService.kitchenTunnelData.durationWithoutInstallation,

      productPrice: tunnelService.kitchenTunnelData.withInstallation
        ? tunnelService.kitchenTunnelData.productPrice
        : tunnelService.kitchenTunnelData.productPriceWithoutInstallation,
      otherProductPrice: !tunnelService.kitchenTunnelData.withInstallation
        ? tunnelService.kitchenTunnelData.productPrice
        : tunnelService.kitchenTunnelData.productPriceWithoutInstallation,

      appliancePrice: tunnelService.kitchenTunnelData.withInstallation
        ? tunnelService.kitchenTunnelData.appliancePrice
        : tunnelService.kitchenTunnelData.appliancePriceWithoutInstallation,
      otherAppliancePrice: !tunnelService.kitchenTunnelData.withInstallation
        ? tunnelService.kitchenTunnelData.appliancePrice
        : tunnelService.kitchenTunnelData.appliancePriceWithoutInstallation,

      totalProductPrice: tunnelService.kitchenTunnelData.withInstallation
        ? tunnelService.kitchenTunnelData.totalProductPrice
        : tunnelService.kitchenTunnelData.totalProductPriceWithoutInstallation,
      otherTotalProductPrice: !tunnelService.kitchenTunnelData.withInstallation
        ? tunnelService.kitchenTunnelData.totalProductPrice
        : tunnelService.kitchenTunnelData.totalProductPriceWithoutInstallation,

      installationPrice: tunnelService.kitchenTunnelData.installationPrice,
      taeg: tunnelService.kitchenTunnelData.withInstallation
        ? tunnelService.kitchenTunnelData.taegWithInstallation
        : tunnelService.kitchenTunnelData.taegWithoutInstallation,

      montantTotal: tunnelService.kitchenTunnelData.withInstallation
        ? tunnelService.kitchenTunnelData.projectTotalPrice
        : tunnelService.kitchenTunnelData.projectTotalWithoutInstallationPrice,
      otherMontantTotal: !tunnelService.kitchenTunnelData.withInstallation
        ? tunnelService.kitchenTunnelData.projectTotalPrice
        : tunnelService.kitchenTunnelData.projectTotalWithoutInstallationPrice,

      title: currentService.HBMProject.name,
      customerId: customerService.userDatas.id,
      creatorId: currentService.vendor.sapId,

      bom: JSON.stringify(tunnelService.kitchenTunnelData.bom),
      installationDetail: JSON.stringify(tunnelService.kitchenTunnelData.detailTable),

      offers: JSON.stringify(
        tunnelService.kitchenTunnelData.withInstallation
          ? tunnelService.kitchenTunnelData.offerTableWithInstallation
          : tunnelService.kitchenTunnelData.offerTableWithoutInstallation
      ),
      otherOffers: JSON.stringify(
        !tunnelService.kitchenTunnelData.withInstallation
          ? tunnelService.kitchenTunnelData.offerTableWithInstallation
          : tunnelService.kitchenTunnelData.offerTableWithoutInstallation
      ),
      txtAssuranceWithInstallation: tunnelService.kitchenTunnelData.txtAssuranceWithInstallation,
      vacWithInstallation: tunnelService.kitchenTunnelData.vacWithInstallation,
      vacWithoutInstallation: tunnelService.kitchenTunnelData.vacWithoutInstallation,
      txtAssuranceWithoutInstallation: tunnelService.kitchenTunnelData.txtAssuranceWithoutInstallation,
      styleId: currentService.kitchen.styleId,
      modelId: currentService.kitchen.modelId,
      layoutRef: currentService.kitchen.layoutRef,
      ovenConfigurationRef: currentService.kitchen.ovenConfigurationRef,
      fridgeConfigurationRef: currentService.kitchen.fridgeConfigurationRef,

      mlData: JSON.stringify(
        tunnelService.kitchenTunnelData.withInstallation
          ? tunnelService.kitchenTunnelData.mlDataWithInstallation
          : tunnelService.kitchenTunnelData.mlDataWithoutInstallation
      ),
      otherMlData: JSON.stringify(
        !tunnelService.kitchenTunnelData.withInstallation
          ? tunnelService.kitchenTunnelData.mlDataWithInstallation
          : tunnelService.kitchenTunnelData.mlDataWithoutInstallation
      ),
      opportunityNumber: tunnelService.kitchenTunnelData.opportunityNumber,
      workbenchProjectUUID: tunnelService.kitchenTunnelData.workbenchProjectUUID,
      workbenchOpportunityNumber: tunnelService.kitchenTunnelData.workbenchOpportunityNumber,
    };

    return kitchenTunnelData;
  }

  private sendSavingCountStatistic() {
    const customerService = this.injector.get(CustomerService);
    const statisticService = this.injector.get(StatisticService);
    //Envoi de la stat de sauvegarde de projet
    statisticService.sendStatistic(StatisticTypeEnum.SAVING_COUNT, 1, customerService.context);
  }

  private subscribeToObservables() {
    this._modelSubscription = this.model$.subscribe(model => {
      if (model) {
        this.model = model;
      }
    });

    this._loadingSubscription = this.loading$.subscribe(loading => (this.loading = loading));
    this._discountValueSubscription = this.discountValueSelected
      .asObservable()
      .subscribe(async (discountValue: DiscountTypeEnum) => {
        if (discountValue) {
          const tunnelService = this.injector.get(TunnelService);
          tunnelService.storageTunnelData.discountValue = discountValue;

          if (!this.featureSettingsService.financementEnabled) {
            this.onRefreshRequested.emit();
            return;
          }

          const requestContent = {
            montant: tunnelService.storageTunnelData.totalPriceBomWithDiscount.discountPrices[discountValue].amount,
            duree: 10,
            duree_marge: 15,
          };
          await this.loadOffers(requestContent);
          this.onRefreshRequested.emit();
        }
      });
  }

  async loadOffers(requestContent) {
    try {
      const responseOffers = await this.storageService.postOffres(requestContent);
      const storageTunnelData = this.injector.get(TunnelService).storageTunnelData;

      const offers = responseOffers?.offers_data;
      if (Array.isArray(offers) && offers.length > 0) {
        const selectedOffer = offers.find(offer => offer.matching === 'offer_best');
        this.selectOffer(selectedOffer);
        storageTunnelData.offerTable = offers;
      }

      // this.sendStatistic();
    } catch (error) {
      logError('Erreur lors du chargement des offres :', error);
    }
  }

  selectOffer(selectedOffer) {
    if (!this.featureSettingsService.financementEnabled) {
      return;
    }

    const storageTunnelData = this.injector.get(TunnelService).storageTunnelData;
    if (selectedOffer) {
      storageTunnelData.duration = selectedOffer['nbecheances'];
      storageTunnelData.mensuality = selectedOffer['mens_1'];
      storageTunnelData.vac = selectedOffer['offre_details']['txt_exemple_vac'];
      storageTunnelData.txtAssurance = selectedOffer['offre_details']['txt_assurance'];
      storageTunnelData.interest = selectedOffer['coutcredit'];
      storageTunnelData.totalFundingCost = selectedOffer['totaldu'] + storageTunnelData.downPayment;

      let requestContent = {
        offre_type: selectedOffer['bareme_type'],
        offre_gratuite: String(selectedOffer['coutcredit'] == 0),
      };

      // this.mlLoading = true;
      this.storageService.postMentionsLegale(requestContent).then(response => {
        if (!response) {
          return;
        }

        storageTunnelData.mlData = JSON.parse(response);
      });
    } else {
      storageTunnelData.duration = undefined;
      storageTunnelData.mensuality = undefined;
      storageTunnelData.totalFundingCost = storageTunnelData.totalPriceBom;
    }
  }

  unlock() {
    const projectService = this.injector.get(ProjectService);
    const currentService = this.injector.get(CurrentService);
    try {
      projectService.unlock(currentService.HBMProject.id);
      currentService.HBMProject.locked = false;
      this.onRefreshRequested.emit(VALIDATION_COMPONENT_TYPE_ENUM.VALIDATION_SIDE_BAR_ACTION);
    } catch (error) {
      this.handleError(error);
    }
  }

  showProductList() {
    const tunnelService = this.injector.get(TunnelService);
    const modalService = this.injector.get(ModalService);
    if (this.isCurrentContextKitchen) {
      modalService.display(MODAL.PRODUCTS_LIST_V2, {
        bom: tunnelService.kitchenTunnelData.bom,
        reloadBom: false,
        isReadOnly: true,
      });
    } else if (this.isCurrentContextStorage) {
      modalService.display(MODAL.PRODUCTS_LIST_V2, {
        bom: tunnelService.storageTunnelData.bom,
        reloadBom: false,
        isReadOnly: true,
      });
    }
  }

  showDiscountChoice() {
    const tunnelService = this.injector.get(TunnelService);
    const modalService = this.injector.get(ModalService);
    modalService.display(MODAL.VALIDATION_DISCOUNT_CHOICE, {
      discountValueSelected: this.discountValueSelected,
      discountValue: tunnelService.storageTunnelData.discountValue,
    });
  }

  /**
   * Appel la méthode pour générer le pdf dans le back et appel la méthode pour l'afficher dans le navigateur.
   */
  async printValidationNew() {
    const storageService = this.injector.get(StorageService);
    this.printLoading = true;
    const pdfViewModel: PdfViewModel = this.buildPdfViewModel();
    try {
      const printService = this.injector.get(PrintService);
      const base64 = await storageService.generateNewPdf(pdfViewModel);
      printService.printPdf(base64.value, true);
    } catch (error) {
      this.handleError(error);
    } finally {
      this.printLoading = false;
    }
  }

  /**
   * Initialise l'objet PdfViewModel
   * @returns
   */
  buildPdfViewModel(): PdfViewModel {
    const tunnelService = this.injector.get(TunnelService);
    const translateService = this.injector.get(TranslateService);
    const customerService = this.injector.get(CustomerService);

    let bom = cloneDeep(tunnelService.storageTunnelData.bom);
    bom.totalByCategories.forEach(x => {
      x.key = translateService.instant('popins.products_list.main.' + x.key + '.title');
    });

    return {
      configContext: customerService.context,
      configurationId: tunnelService.kitchenTunnelData.configId,
      configurationId2D: tunnelService.workbenchTunnelData.id,
      customerContext: translateService.instant('popins.products_list.print_title_' + customerService.context),
      disclaimer: translateService.instant('popins.products_list.aside.disclaimerPdf'),
      rootBom: bom,
      fidelityCardPoints: this.featureSettingsService.fidelityEnabled
        ? tunnelService.storageTunnelData.fidelityCardPoint
        : [],
      kitchenOfferConfiguration: null,
      storageOfferConfiguration: {
        discount: Number.parseInt(tunnelService.storageTunnelData.discountValue.match(/\d+/)[0], 10),
        downPayment: tunnelService.storageTunnelData.downPayment,
        totalPriceBom:
          tunnelService.storageTunnelData.totalPriceBomWithDiscount.discountPrices[
            tunnelService.storageTunnelData.discountValue
          ].amount,
        montantTotal: tunnelService.storageTunnelData.totalFundingCost,
        financementDetail: this.featureSettingsService.financementEnabled
          ? {
              mensuality: tunnelService.storageTunnelData.mensuality,
              duration: tunnelService.storageTunnelData.duration,
              vac: tunnelService.storageTunnelData.vac,
              txtAssurance: tunnelService.storageTunnelData.txtAssurance,
              offers: tunnelService.storageTunnelData.offerTable,
              mlCasto: tunnelService.storageTunnelData.mlData?.ml_casto,
              mlSofinco: tunnelService.storageTunnelData.mlData?.ml_sofinco,
              interest: tunnelService.storageTunnelData.interest,
            }
          : null,
      },
      ProjectImageUrl: this.model.projectImageUrl,
    };
  }

  private get priceKey() {
    const tunnelService = this.injector.get(TunnelService);
    return tunnelService?.kitchenTunnelData?.isLess2Years ? 'moins2' : 'plus2';
  }

  private showSuccessToast(message: string) {
    const toastService = this.injector.get(ToastService);
    const translateService = this.injector.get(TranslateService);

    toastService.showSuccessToast(translateService.instant('validation.toast.success_title'), message);
  }

  private showErrorToast(message: string) {
    const toastService = this.injector.get(ToastService);
    const translateService = this.injector.get(TranslateService);

    toastService.showErrorToast(translateService.instant('validation.toast.error_title'), message);
  }

  checkFidIsUndefined() {
    if (!this.featureSettingsService.fidelityEnabled) {
      return false;
    }

    const tunnelService = this.injector.get(TunnelService);
    return (
      tunnelService.storageTunnelData.fidelityCardPoint === undefined ||
      tunnelService.storageTunnelData.fidelityCardPoint.length === 0
    );
  }

  get featureWarningMessages(): string[] {
    const translateService = this.injector.get(TranslateService);
    const messages: string[] = [];

    if (!this.featureSettingsService.fidelityEnabled) {
      messages.push(translateService.instant('feature-settings.warning.fidelity'));
    }

    if (!this.featureSettingsService.poseEnabled) {
      messages.push(translateService.instant('feature-settings.warning.pose'));
    }

    if (!this.featureSettingsService.financementEnabled) {
      messages.push(translateService.instant('feature-settings.warning.financement'));
    }

    return messages;
  }
}
